import { memo, useState } from "react";
import { MessageCircle, Zap, MapPin, Globe, Sparkles, Clock, Video, Gamepad2, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { CompatibilityBadge } from "./CompatibilityBadge";
import { VibeEchoTag } from "./VibeEchoTag";
import { CaptureSparkReel } from "./CaptureSparkReel";

interface MatchCardProps {
  match: {
    id: string;
    match_source: string;
    created_at: string;
    met_context: string | null;
    source_context: Record<string, unknown> | null;
    global_connect_country: string | null;
    matchedProfile: {
      id: string;
      name: string;
      age: number | null;
      photo_url: string | null;
      is_verified: boolean | null;
    } | null;
    lastMessage?: string | null;
    unreadCount?: number;
    isOnline?: boolean;
    lastSeen?: string | null;
    compatibility?: {
      score: number;
      sharedInterests: string[];
      totalShared: number;
    };
  };
  index: number;
  onOpenChat: () => void;
  onOpenPortal: () => void;
  onVideoChat?: () => void;
  onVirtualDate?: () => void;
}

const SOURCE_STYLES: Record<string, { gradient: string; badge: string; icon: React.ReactNode }> = {
  exhibition: {
    gradient: "from-amber-500/10 via-transparent to-orange-500/5",
    badge: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    icon: <Sparkles className="w-3 h-3" />,
  },
  pop_balloon: {
    gradient: "from-pink-500/10 via-transparent to-rose-500/5",
    badge: "bg-pink-500/15 text-pink-600 border-pink-500/30",
    icon: <span className="text-xs">🎈</span>,
  },
  global_connect: {
    gradient: "from-blue-500/10 via-transparent to-cyan-500/5",
    badge: "bg-blue-500/15 text-blue-600 border-blue-500/30",
    icon: <Globe className="w-3 h-3" />,
  },
};

const SOURCE_LABELS: Record<string, string> = {
  exhibition: "Exhibition",
  pop_balloon: "Pop Balloon",
  global_connect: "Global Connect",
};

export const getContextDisplay = (match: MatchCardProps["match"]) => {
  const context = match.source_context as Record<string, string> | null;
  
  if (match.match_source === "exhibition") {
    const boothName = context?.booth_name || context?.arena_name;
    const cityName = context?.city_name;
    if (boothName && cityName) return `${boothName} • ${cityName}`;
    if (boothName) return boothName;
    if (cityName) return cityName;
    return "Love Exhibition";
  }
  
  if (match.match_source === "global_connect") {
    const scene = context?.scene;
    const cityName = context?.city_name;
    const country = match.global_connect_country;
    if (scene && cityName) return `${scene} • ${cityName}`;
    if (cityName) return cityName;
    if (country) return country;
    return "Global Connect";
  }
  
  return null;
};

export const MatchCard = memo(({ match, index, onOpenChat, onOpenPortal, onVideoChat, onVirtualDate }: MatchCardProps) => {
  const [showCaptureReel, setShowCaptureReel] = useState(false);
  const profile = match.matchedProfile;
  if (!profile) return null;

  const sourceStyle = SOURCE_STYLES[match.match_source] || SOURCE_STYLES.pop_balloon;
  const sourceLabel = SOURCE_LABELS[match.match_source] || "Match";
  const contextDisplay = getContextDisplay(match);
  const timeAgo = formatDistanceToNow(new Date(match.created_at), { addSuffix: true });
  const showVideoNudge = (match.match_source === "exhibition" || match.match_source === "global_connect") && onVideoChat;

  // Determine glow ring color based on source
  const glowRingColor = match.match_source === "exhibition" 
    ? "ring-amber-500/30" 
    : match.match_source === "global_connect"
    ? "ring-blue-500/30"
    : "ring-pink-500/30";

  // Mock resonance data (in production, this would come from match data)
  const resonance = match.source_context?.resonance_detected 
    ? {
        type: "rhythm" as const,
        description: "Shared morning coffee rhythm",
      }
    : undefined;

  return (
    <>
      <div
        className={cn(
          "group relative overflow-hidden rounded-2xl transition-all duration-300",
          "bg-card/80 backdrop-blur-sm border border-border/50",
          "hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5",
          "hover:scale-[1.02] hover:-translate-y-1",
          "animate-fade-in-up"
        )}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        {/* Floating orb effect - source gradient overlay */}
        <div className={cn("absolute inset-0 bg-gradient-to-r opacity-50", sourceStyle.gradient)} />
        
        {/* Source glow ring around avatar */}
        <div className={cn("absolute inset-0 rounded-2xl", glowRingColor, "ring-2 opacity-0 group-hover:opacity-100 transition-opacity")} />

      {/* Content */}
      <div className="relative flex items-center gap-4 p-4 cursor-pointer" onClick={onOpenChat}>
        {/* Avatar with online indicator and glow ring */}
        <div className="relative shrink-0">
          <div className={cn(
            "w-16 h-16 rounded-full overflow-hidden ring-2 ring-background shadow-md",
            "relative group-hover:ring-4 transition-all duration-300",
            glowRingColor.replace("ring-2", "ring-4")
          )}>
            {profile.photo_url ? (
              <img 
                src={profile.photo_url} 
                alt={profile.name} 
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" 
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-muted-foreground bg-gradient-to-br from-muted to-muted/50">
                {profile.name?.[0]}
              </div>
            )}
          </div>
          
          {/* Online indicator */}
          {match.isOnline && (
            <div className="absolute -bottom-0.5 -right-0.5 flex items-center">
              <div className="w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-card animate-pulse" />
            </div>
          )}
          
          {/* Unread badge */}
          {match.unreadCount && match.unreadCount > 0 && (
            <div className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center shadow-sm animate-bounce-in">
              {match.unreadCount > 9 ? "9+" : match.unreadCount}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground truncate">
              {profile.name}{profile.age ? `, ${profile.age}` : ""}
            </h3>
            {profile.is_verified && (
              <span className="text-blue-500 text-sm">✓</span>
            )}
            {/* Online status text */}
            {match.isOnline && (
              <span className="text-[10px] font-medium text-green-500">Online</span>
            )}
          </div>

          {/* Context / Where you met */}
          {contextDisplay && (
            <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{contextDisplay}</span>
            </div>
          )}

          {/* Last message preview OR time ago */}
          <div className="mt-1.5 flex items-center gap-2">
            {match.lastMessage ? (
              <p className={cn(
                "text-sm truncate max-w-[180px]",
                match.unreadCount && match.unreadCount > 0 
                  ? "text-foreground font-medium" 
                  : "text-muted-foreground"
              )}>
                {match.lastMessage}
              </p>
            ) : (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>Matched {timeAgo}</span>
              </div>
            )}
          </div>

          {/* Source badge + Compatibility + Vibe Echo */}
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span className={cn(
              "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border",
              sourceStyle.badge
            )}>
              {sourceStyle.icon}
              {sourceLabel}
            </span>
            
            {/* Compatibility badge */}
            {match.compatibility && match.compatibility.score > 0 && (
              <CompatibilityBadge
                score={match.compatibility.score}
                sharedInterests={match.compatibility.sharedInterests}
                totalShared={match.compatibility.totalShared}
                size="sm"
              />
            )}

            {/* Vibe Echo Tag */}
            {resonance && <VibeEchoTag resonance={resonance} />}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Capture Our Spark button */}
          <Button
            variant="ghost"
            size="icon"
            className="w-10 h-10 rounded-full hover:bg-pink-500/10 text-pink-500"
            title="Capture Our Spark"
            onClick={(e) => {
              e.stopPropagation();
              setShowCaptureReel(true);
            }}
          >
            <Heart className="w-4 h-4" />
          </Button>
          
          {showVideoNudge && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "w-10 h-10 rounded-full",
                match.match_source === "exhibition" 
                  ? "hover:bg-amber-500/10 text-amber-500" 
                  : "hover:bg-blue-500/10 text-blue-500"
              )}
              title="Video Chat"
              onClick={(e) => {
                e.stopPropagation();
                onVideoChat?.();
              }}
            >
              <Video className="w-4 h-4" />
            </Button>
          )}
          {onVirtualDate && (
            <Button
              variant="ghost"
              size="icon"
              className="w-10 h-10 rounded-full hover:bg-green-500/10 text-green-500"
              title="Virtual Date"
              onClick={(e) => {
                e.stopPropagation();
                onVirtualDate();
              }}
            >
              <Gamepad2 className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="w-10 h-10 rounded-full hover:bg-primary/10"
            title="Open Portal"
            onClick={(e) => {
              e.stopPropagation();
              onOpenPortal();
            }}
          >
            <Zap className="w-4 h-4 text-primary" />
          </Button>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground shadow-md group-hover:scale-105 transition-transform">
            <MessageCircle className="w-5 h-5" />
          </div>
        </div>
      </div>
      </div>

      {/* Capture Spark Reel Modal */}
      {showCaptureReel && (
        <CaptureSparkReel
          match={match}
          firstMessage={match.lastMessage || undefined}
          onClose={() => setShowCaptureReel(false)}
        />
      )}
    </>
  );
});

MatchCard.displayName = "MatchCard";
